import { SESSION_QUESTION_TARGET, TOPICS } from '../data/topics';
import { MOCK_FORM_COUNT } from '../constants/mockForms';
import frozenAssignments from '../data/mockFormAssignments.json';

export function shuffle(array, rng = Math.random) {
  const next = [...array];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

/** Deterministic PRNG (mulberry32) so each mock form is a stable composition. */
export function createSeededRng(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashString(input) {
  const s = String(input || '');
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Prefer this form when partitioning the bank (0-based form index). */
export function preferredFormIndex(question, formCount = MOCK_FORM_COUNT) {
  const id = questionId(question);
  const key = id != null && id !== '' ? String(id) : stemFingerprint(question);
  return hashString(key) % formCount;
}

/** 1-based mock form for a question: the frozen map (src/data/mockFormAssignments.json) first, else a stable id hash. */
export function assignedForm(question, assignments = frozenAssignments, formCount = MOCK_FORM_COUNT) {
  const id = questionId(question);
  const fixed = id != null ? assignments?.[id] : undefined;
  if (Number.isInteger(fixed) && fixed >= 1 && fixed <= formCount) return fixed;
  return preferredFormIndex(question, Math.min(formCount, 4)) + 1;
}

function isFrozen(question, assignments) {
  const id = questionId(question);
  return id != null && Number.isInteger(assignments?.[id]);
}

function hashKeyOrder(a, b) {
  const ka = String(questionId(a) ?? stemFingerprint(a));
  const kb = String(questionId(b) ?? stemFingerprint(b));
  const ha = hashString(ka);
  const hb = hashString(kb);
  if (ha !== hb) return ha - hb;
  return ka.localeCompare(kb);
}

/**
 * Per-topic order for a mock form. Named forms take, in order:
 *   1. questions frozen to this form,
 *   2. unfrozen questions (new items) whose id hash lands on this form,
 *   3. other unfrozen questions,
 *   4. questions frozen to other forms (only if a topic would otherwise be short).
 * Membership never depends on how many other questions exist, so adding or
 * removing one question cannot reshuffle the mocks. `partial` (a first, small
 * chunk of the bank) keeps only group 1, so a mock never starts with another
 * form's questions. Practice (formId 0) is a plain shuffle.
 */
export function orderedTopicPool(
  list,
  { formId = 0, formCount = MOCK_FORM_COUNT, rng = Math.random, assignments = frozenAssignments, partial = false } = {}
) {
  const sorted = [...list].sort(hashKeyOrder);
  const id = Number(formId) || 0;
  if (id < 1) return shuffle(sorted, rng);
  const groups = [[], [], [], []];
  sorted.forEach((q) => {
    const frozen = isFrozen(q, assignments);
    const mine = assignedForm(q, assignments, formCount) === id;
    if (frozen) groups[mine ? 0 : 3].push(q);
    else groups[mine ? 1 : 2].push(q);
  });
  return partial ? groups[0] : groups.flat();
}

export function questionId(q) {
  return q?.id ?? q?.question;
}

/** Normalize stem so local seed copies and Firestore docs of the same item collide. */
export function stemFingerprint(question) {
  return String(question?.question || question || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 120);
}

function preferQuestion(a, b) {
  const aId = a?.id != null ? String(a.id) : '';
  const bId = b?.id != null ? String(b.id) : '';
  const aLocal = /^\d+$/.test(aId);
  const bLocal = /^\d+$/.test(bId);
  if (aLocal !== bLocal) return bLocal ? a : b;
  if (aId.length !== bId.length) return aId.length >= bId.length ? a : b;
  return a;
}

/** One item per id and per stem (keeps Firestore over numeric local seed ids). */
export function dedupeQuestions(questions = []) {
  const byId = new Map();
  const byStem = new Map();

  questions.forEach((q) => {
    if (!q) return;
    const id = questionId(q);
    if (id != null && id !== '') {
      const prev = byId.get(id);
      byId.set(id, prev ? preferQuestion(prev, q) : q);
      return;
    }
    const stem = stemFingerprint(q);
    if (!stem) return;
    const prev = byStem.get(stem);
    byStem.set(stem, prev ? preferQuestion(prev, q) : q);
  });

  const out = [];
  const seenStem = new Set();
  byId.forEach((q) => {
    const stem = stemFingerprint(q);
    if (stem && seenStem.has(stem)) return;
    if (stem) seenStem.add(stem);
    out.push(q);
  });
  byStem.forEach((q, stem) => {
    if (seenStem.has(stem)) return;
    seenStem.add(stem);
    out.push(q);
  });
  return out;
}

function markUsed(usedIds, usedStems, q) {
  const id = questionId(q);
  if (id != null && id !== '') usedIds.add(id);
  const stem = stemFingerprint(q);
  if (stem) usedStems.add(stem);
}

function isUsed(usedIds, usedStems, q) {
  const id = questionId(q);
  if (id != null && id !== '' && usedIds.has(id)) return true;
  const stem = stemFingerprint(q);
  return Boolean(stem && usedStems.has(stem));
}

/** Drop id/stem collisions while preserving first occurrence order. */
export function ensureUniqueQuestions(questions = []) {
  const usedIds = new Set();
  const usedStems = new Set();
  const out = [];
  questions.forEach((q) => {
    if (!q || isUsed(usedIds, usedStems, q)) return;
    out.push(q);
    markUsed(usedIds, usedStems, q);
  });
  return out;
}

/**
 * Build a session-sized mock using official-ish Level 1 weights.
 * If the bank is smaller than 90, returns the full shuffled bank.
 * Dedupes by id and stem so local seed + Firestore copies cannot both appear.
 *
 * @param {object[]} questions
 * @param {number} [target]
 * @param {{ formId?: number }} [options] — when formId is a named form, composition is seeded
 *   and prefers items partitioned to that form (maximizes distinctness across mocks).
 */
export function sampleSession(questions, target = SESSION_QUESTION_TARGET, options = {}) {
  const formId = Number(options.formId) || 0;
  const rng = formId
    ? createSeededRng(hashString(`cfa-mock-form-${formId}-v1`))
    : Math.random;

  let unique = dedupeQuestions(questions);
  if (formId && options.partial) {
    // First small chunk of the bank: start only with this form's own questions.
    const map = options.assignments || frozenAssignments;
    unique = unique.filter((q) => isFrozen(q, map) && assignedForm(q, map) === formId);
  }
  if (!unique.length) return [];
  if (unique.length <= target) return shuffle(unique, rng);

  const byTopic = new Map();
  unique.forEach((q) => {
    const key = q.topic || 'Other';
    if (!byTopic.has(key)) byTopic.set(key, []);
    byTopic.get(key).push(q);
  });

  byTopic.forEach((list, key) => {
    byTopic.set(
      key,
      orderedTopicPool(list, {
        formId,
        formCount: MOCK_FORM_COUNT,
        rng,
        ...(options.assignments ? { assignments: options.assignments } : {}),
        partial: !!options.partial,
      })
    );
  });

  const picked = [];
  const usedIds = new Set();
  const usedStems = new Set();

  TOPICS.forEach((topic) => {
    const pool = byTopic.get(topic.firestore) || [];
    let taken = 0;
    for (const q of pool) {
      if (picked.length >= target || taken >= topic.sessionCount) break;
      if (isUsed(usedIds, usedStems, q)) continue;
      picked.push(q);
      markUsed(usedIds, usedStems, q);
      taken += 1;
    }
  });

  // Never pad with arbitrary topics — that breaks official weight ranges.
  // 1) Finish any topic still short of its sessionCount.
  // 2) Only then allow overflow up to floor(weightMax * target) per topic.
  const countByTopic = () => {
    const counts = Object.fromEntries(TOPICS.map((t) => [t.firestore, 0]));
    picked.forEach((q) => {
      if (counts[q.topic] != null) counts[q.topic] += 1;
    });
    return counts;
  };

  const takeFromTopic = (topicName, want) => {
    if (want <= 0 || picked.length >= target) return;
    const pool = byTopic.get(topicName) || [];
    let taken = 0;
    for (const q of pool) {
      if (picked.length >= target || taken >= want) break;
      if (isUsed(usedIds, usedStems, q)) continue;
      picked.push(q);
      markUsed(usedIds, usedStems, q);
      taken += 1;
    }
  };

  TOPICS.forEach((topic) => {
    const have = countByTopic()[topic.firestore] || 0;
    takeFromTopic(topic.firestore, topic.sessionCount - have);
  });

  if (picked.length < target) {
    TOPICS.forEach((topic) => {
      const maxAllowed = Math.floor((topic.weightMax || topic.weight || 0) * target);
      const cap = Math.max(topic.sessionCount, maxAllowed || topic.sessionCount);
      const have = countByTopic()[topic.firestore] || 0;
      takeFromTopic(topic.firestore, cap - have);
    });
  }

  return ensureUniqueQuestions(shuffle(picked, rng)).slice(0, target);
}

/** Append into an in-progress session without reshuffling items already shown. */
export function extendSession(picked, pool, target = SESSION_QUESTION_TARGET, options = {}) {
  if (picked.length >= target) return picked;
  const formId = Number(options.formId) || 0;
  const rng = formId
    ? createSeededRng(hashString(`cfa-mock-form-${formId}-extend-v1`))
    : Math.random;

  const usedIds = new Set();
  const usedStems = new Set();
  picked.forEach((q) => markUsed(usedIds, usedStems, q));

  const counts = Object.fromEntries(TOPICS.map((t) => [t.firestore, 0]));
  picked.forEach((q) => {
    if (counts[q.topic] != null) counts[q.topic] += 1;
  });

  const next = [...picked];
  const uniquePool = dedupeQuestions(pool);
  const byTopic = new Map();
  uniquePool.forEach((q) => {
    if (isUsed(usedIds, usedStems, q)) return;
    const key = q.topic || 'Other';
    if (!byTopic.has(key)) byTopic.set(key, []);
    byTopic.get(key).push(q);
  });
  byTopic.forEach((list, key) => {
    byTopic.set(
      key,
      orderedTopicPool(list, {
        formId,
        formCount: MOCK_FORM_COUNT,
        rng,
        ...(options.assignments ? { assignments: options.assignments } : {}),
        partial: !!(formId && options.partial),
      })
    );
  });

  TOPICS.forEach((topic) => {
    const room = target - next.length;
    if (room <= 0) return;
    const want = Math.max(0, topic.sessionCount - (counts[topic.firestore] || 0));
    const take = (byTopic.get(topic.firestore) || []).slice(0, Math.min(want, room));
    take.forEach((q) => {
      if (isUsed(usedIds, usedStems, q)) return;
      next.push(q);
      markUsed(usedIds, usedStems, q);
      counts[topic.firestore] = (counts[topic.firestore] || 0) + 1;
    });
  });

  // Weight-safe top-up only (same rules as sampleSession).
  const takeMore = (topic, want) => {
    if (want <= 0 || next.length >= target) return;
    const pool = byTopic.get(topic.firestore) || [];
    let taken = 0;
    for (const q of pool) {
      if (next.length >= target || taken >= want) break;
      if (isUsed(usedIds, usedStems, q)) continue;
      next.push(q);
      markUsed(usedIds, usedStems, q);
      counts[topic.firestore] = (counts[topic.firestore] || 0) + 1;
      taken += 1;
    }
  };

  TOPICS.forEach((topic) => {
    takeMore(topic, topic.sessionCount - (counts[topic.firestore] || 0));
  });

  if (next.length < target) {
    TOPICS.forEach((topic) => {
      const maxAllowed = Math.floor((topic.weightMax || topic.weight || 0) * target);
      const cap = Math.max(topic.sessionCount, maxAllowed || topic.sessionCount);
      takeMore(topic, cap - (counts[topic.firestore] || 0));
    });
  }

  return ensureUniqueQuestions(next);
}
export function sessionTopicCounts(questions = []) {
  const counts = Object.fromEntries(TOPICS.map((t) => [t.firestore, 0]));
  questions.forEach((q) => {
    if (counts[q.topic] != null) counts[q.topic] += 1;
  });
  return counts;
}

/**
 * True when every topic is at its sessionCount target (exact official mock mix).
 * Returns { ok, missing, extras } for diagnostics.
 */
export function validateSessionWeights(questions = [], target = SESSION_QUESTION_TARGET) {
  const counts = sessionTopicCounts(questions);
  const missing = [];
  const extras = [];
  TOPICS.forEach((topic) => {
    const have = counts[topic.firestore] || 0;
    if (have < topic.sessionCount) missing.push({ topic: topic.firestore, have, want: topic.sessionCount });
    if (have > topic.sessionCount) extras.push({ topic: topic.firestore, have, want: topic.sessionCount });
  });
  const total = questions.length;
  const ok = total === target && missing.length === 0 && extras.length === 0;
  return { ok, total, target, counts, missing, extras };
}

export function answersMatch(selected, correct) {
  if (selected === null || selected === undefined || selected === '') return false;
  return String(selected) === String(correct);
}

/** Constructed-response / essay items are reviewed against a model answer, not auto-scored. */
export function isAutoGradedQuestion(question) {
  if (!question) return true;
  if (question.autoGrade === false) return false;
  if (String(question.format || '').toLowerCase() === 'constructed') return false;
  return true;
}
